import {
  Box,
  Collapse,
  Fade,
  Typography,
  useMediaQuery
} from "@mui/material";
import { t } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext";

type QuoteEntry = {
  text: string;
  author: string;
};

// number of quotes visible at once
const visibleCount = 4;
// duration of scroll animation
const quoteTransitionDuration = 1000;

// time each quote set stays fully visible before scrolling
const quoteFullyVisibleMs = 3200;

export const Testimonials = () => {
  const { theme } = useTheme();
  const isSmallDisplay = useMediaQuery(theme.breakpoints.down("sm"));

  const quotes: QuoteEntry[] =
    (t("login.quotes", { returnObjects: true }) as QuoteEntry[]) ?? [];
  const totalCount = quotes.length;

  const rowHeight = isSmallDisplay
    ? parseFloat(theme.spacing(16))
    : parseFloat(theme.spacing(18));
  const [index, setIndex] = useState(0);

  const [animating, setAnimating] = useState(false);
  const animationRef = useRef(animating);
  animationRef.current = animating;

  // indices of the currently visible window of quotes
  const windowIndex = useMemo(
    () =>
      Array.from({ length: visibleCount }, (_, i) => (index + i) % totalCount),
    [index, totalCount]
  );

  // index of the next incoming row for the bottom during animation
  const incomingIndex = (index + visibleCount) % totalCount;

  // keep the timer ID in a ref so we can stop/restart it
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!totalCount) return;

    // triggers animation if we're not already animating
    const tick = () => {
      if (!animationRef.current) setAnimating(true);
    };

    // start the auto-scroll timer
    const startTimer = () => {
      stopTimer();
      timerRef.current = window.setInterval(
        tick,
        quoteFullyVisibleMs + quoteTransitionDuration
      );
    };

    // stop the timer
    const stopTimer = () => {
      if (timerRef.current !== undefined) {
        window.clearInterval(timerRef.current);
      }
    };

    // pause/resume when the page/tab visibility changes
    const onVis = () => {
      if (document.visibilityState === "hidden") stopTimer();
      else startTimer();
    };

    startTimer();
    document.addEventListener("visibilitychange", onVis);

    return () => {
      stopTimer();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [totalCount]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: theme.spacing(2),
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 820,
          height: rowHeight * visibleCount,
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {windowIndex.map((quoteIdx, i) => {
            // top row collapses during animation, others stay fixed
            const isTop = i === 0;

            return (
              <Collapse
                key={`row-${quoteIdx}-${index}`}
                in={!(animating && isTop)}
                timeout={quoteTransitionDuration}
                collapsedSize={0}
                // once the top finishes collapsing, commit the new index & end animation
                onExited={
                  isTop
                    ? () => {
                        setIndex((s) => (s + 1) % totalCount);
                        setAnimating(false);
                      }
                    : undefined
                }
                sx={{ height: animating && isTop ? undefined : rowHeight }}
              >
                <Fade in={!(animating && isTop)} appear={false}>
                  <Box
                    sx={{
                      height: rowHeight,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      px: theme.spacing(3),
                      textAlign: "center",
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      variant={isSmallDisplay ? "h5" : "h4"}
                      sx={{
                        color: "text.secondary",
                        fontStyle: "italic",
                      }}
                    >
                      “{quotes[quoteIdx]?.text}”
                    </Typography>
                    <Typography
                      variant={isSmallDisplay ? "body2" : "h6"}
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {quotes[quoteIdx]?.author}
                    </Typography>
                  </Box>
                </Fade>
              </Collapse>
            );
          })}

          {/* incoming bottom row expands during animation */}
          <Collapse
            in={animating}
            timeout={quoteTransitionDuration}
            collapsedSize={0}
            unmountOnExit
          >
            <Box
              sx={{
                height: rowHeight,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: theme.spacing(2),
                textAlign: "center",
              }}
            >
              <Typography
                variant={isSmallDisplay ? "h5" : "h4"}
                sx={{
                  color: "text.secondary",
                  fontStyle: "italic",
                }}
              >
                “{quotes[incomingIndex]?.text}”
              </Typography>
              <Typography
                variant={isSmallDisplay ? "body2" : "h6"}
                sx={{
                  color: "text.secondary",
                }}
              >
                {quotes[incomingIndex]?.author}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
};
